const extractLastOperand = (expression: string): string => {
  if (!expression) return '';

  const operators = ['+', '-', '×', '÷'];
  let lastOpIndex = -1;

  let depth = 0;
  for (let i = expression.length - 1; i >= 0; i--) {
    if (expression[i] === ')') {
      depth++;
    } else if (expression[i] === '(') {
      depth--;
    } else if (depth === 0 && operators.includes(expression[i])) {
      if (expression[i] === '-') {
        if (i === 0 || operators.includes(expression[i - 1])) {
          continue;
        }
      }
      lastOpIndex = i;
      break;
    }
  }

  if (lastOpIndex !== -1) {
    return expression.substring(lastOpIndex + 1);
  }

  return expression;
};

export const wrapWithFunction = (currentExpression: string, functionName: string): string => {
  if (!currentExpression || currentExpression === '0') {
    return `${functionName}(`;
  }

  const lastOperand = extractLastOperand(currentExpression);

  if (!lastOperand || lastOperand.trim() === '') {
    return currentExpression + functionName + '(';
  }

  let depth = 0;
  let maxDepth = 0;
  let deepestOpenPos = -1;

  for (let i = 0; i < lastOperand.length; i++) {
    if (lastOperand[i] === '(') {
      depth++;
      if (depth > maxDepth) {
        maxDepth = depth;
        deepestOpenPos = i;
      }
    } else if (lastOperand[i] === ')') {
      depth--;
    }
  }

  const beforeLast = currentExpression.slice(0, currentExpression.length - lastOperand.length);

  if (depth > 0 && deepestOpenPos !== -1) {
    const before = lastOperand.slice(0, deepestOpenPos + 1);
    const after = lastOperand.slice(deepestOpenPos + 1);
    return `${beforeLast}${before}${functionName}(${after}`;
  }

  return `${beforeLast}${functionName}(${lastOperand})`;
};

const isFullyWrapped = (expr: string): boolean => {
  if (!expr.startsWith('(') || !expr.endsWith(')')) {
    return false;
  }

  let depth = 0;
  for (let i = 0; i < expr.length; i++) {
    if (expr[i] === '(') depth++;
    else if (expr[i] === ')') depth--;

    if (depth === 0 && i < expr.length - 1) {
      return false;
    }
  }

  return true;
};

const isValidPostfixOperand = (op: string): boolean => {
  if (!op || op.trim() === '') return false;
  const lastChar = op.trim().slice(-1);
  if (['(', '+', '-', '×', '÷', '^', '√'].includes(lastChar)) return false;
  return true;
};

export const applyPower = (currentExpression: string, power: number): string => {
  if (!currentExpression || currentExpression === '0') {
    return currentExpression || '0';
  }

  const lastOperand = extractLastOperand(currentExpression);

  if (!isValidPostfixOperand(lastOperand)) {
    return currentExpression;
  }

  const beforeLast = currentExpression.slice(0, currentExpression.length - lastOperand.length);

  if (isFullyWrapped(lastOperand)) {
    return `${beforeLast}${lastOperand}^(${power})`;
  }

  const needsParens =
    lastOperand.includes('+') ||
    lastOperand.includes('-') ||
    lastOperand.includes('×') ||
    lastOperand.includes('÷') ||
    lastOperand.includes('^');

  const base = needsParens ? `(${lastOperand})` : lastOperand;
  return `${beforeLast}${base}^(${power})`;
};

export const applyFactorial = (currentExpression: string): string => {
  if (!currentExpression || currentExpression === '0') {
    return currentExpression || '0';
  }

  const lastOperand = extractLastOperand(currentExpression);

  if (!lastOperand || lastOperand.trim() === '') {
    return currentExpression;
  }

  const beforeLast = currentExpression.slice(0, currentExpression.length - lastOperand.length);

  if (isFullyWrapped(lastOperand)) {
    return `${beforeLast}${lastOperand}!`;
  }

  const needsParens =
    lastOperand.includes('+') ||
    lastOperand.includes('-') ||
    lastOperand.includes('×') ||
    lastOperand.includes('÷') ||
    lastOperand.includes('!') ||
    lastOperand.includes('^');

  const base = needsParens ? `(${lastOperand})` : lastOperand;
  return `${beforeLast}${base}!`;
};

export const applyReciprocal = (currentExpression: string): string => {
  if (!currentExpression || currentExpression === '0') {
    return '1÷';
  }

  const lastOperand = extractLastOperand(currentExpression);

  if (!lastOperand || lastOperand.trim() === '') {
    return currentExpression + '1÷';
  }

  const beforeLast = currentExpression.slice(0, currentExpression.length - lastOperand.length);
  return `${beforeLast}1÷${lastOperand}`;
};

export const applyExponential = (currentExpression: string): string => {
  if (!currentExpression || currentExpression === '0') {
    return currentExpression || '0';
  }

  const lastOperand = extractLastOperand(currentExpression);

  if (!isValidPostfixOperand(lastOperand)) {
    return currentExpression;
  }

  const beforeLast = currentExpression.slice(0, currentExpression.length - lastOperand.length);

  // Always wrap the last operand in e^(...) for unlimited recursive nesting
  return `${beforeLast}e^(${lastOperand})`;
};

export const applyPlusMinus = (currentExpression: string): string => {
  if (!currentExpression || currentExpression === '0') {
    return currentExpression || '0';
  }

  const lastOperand = extractLastOperand(currentExpression);

  if (!lastOperand || lastOperand.trim() === '') {
    return currentExpression;
  }

  const beforeLast = currentExpression.slice(0, currentExpression.length - lastOperand.length);

  if (lastOperand.startsWith('(-') && lastOperand.endsWith(')')) {
    return beforeLast + lastOperand.slice(2, -1);
  }

  if (lastOperand.startsWith('-')) {
    return beforeLast + lastOperand.slice(1);
  }

  return `${beforeLast}(-${lastOperand})`;
};

export const insertOpenBracket = (currentExpression: string): string => {
  if (!currentExpression || currentExpression === '0') {
    return '(';
  }

  return currentExpression + '(';
};

export const insertCloseBracket = (currentExpression: string): string => {
  if (!currentExpression) return currentExpression;

  let openCount = 0;
  for (const char of currentExpression) {
    if (char === '(') openCount++;
    else if (char === ')') openCount--;
  }

  if (openCount <= 0) {
    return currentExpression;
  }

  return currentExpression + ')';
};

export const closeAllParentheses = (expression: string): string => {
  if (!expression) return expression;

  let openCount = 0;
  for (const char of expression) {
    if (char === '(') openCount++;
    else if (char === ')') openCount--;
  }

  return expression + ')'.repeat(Math.max(0, openCount));
};

export const handleScientificBackspace = (expression: string): string => {
  if (!expression) return expression;

  if (expression.endsWith('pi')) {
    return expression.slice(0, -2);
  }

  const powerMatch = expression.match(/\^\((\d+)\)$/);
  if (powerMatch) {
    return expression.slice(0, -powerMatch[0].length);
  }

  const functions = ['sin(', 'cos(', 'tan(', 'asin(', 'acos(', 'atan(', 'csc(', 'sec(', 'cot(', 'sqrt('];
  for (const func of functions) {
    if (expression.endsWith(func)) {
      return expression.slice(0, -func.length);
    }
  }

  const lastOperand = extractLastOperand(expression);
  if (lastOperand && lastOperand.startsWith('e^(') && lastOperand.endsWith(')')) {
    const before = expression.slice(0, expression.length - lastOperand.length);
    const inner = lastOperand.slice(3, -1);
    return before + inner;
  }

  return expression.slice(0, -1);
};
